import React from 'react';
import { FilterColumnProps } from '../types';
import { FilterOperator } from '../../../interfaces/sessions';
import { Typography } from '../../../interfaces/typography';

export function DurationFilter({ value, onChange }: FilterColumnProps) {
  const operator = value.operator;
  const currentValue = value.value_safe;

  // Convert seconds to minutes and seconds for display
  const secondsToDisplay = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return { minutes: '', seconds: '' };
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return { minutes: mins.toString(), seconds: secs.toString() };
  };

  // Convert minutes and seconds back to total seconds
  const displayToSeconds = (minutes: string, seconds: string) => {
    const mins = parseInt(minutes) || 0;
    const secs = parseInt(seconds) || 0;
    return mins * 60 + secs;
  };

  const isRangeOperator = [FilterOperator.GT, FilterOperator.GE, FilterOperator.LT, FilterOperator.LE, FilterOperator.EQ, FilterOperator.NEQ].includes(operator);
  const isMultiValueOperator = [FilterOperator.IN, FilterOperator.NIN].includes(operator);

  const handleSingleValueChange = (minutes: string, seconds: string) => {
    const totalSeconds = displayToSeconds(minutes, seconds);
    onChange({
      ...value,
      value_safe: totalSeconds === 0 ? null : totalSeconds
    });
  };

  const handleMultiValueChange = (newValue: string) => {
    // Parse comma-separated duration values (in seconds)
    const values = newValue
      .split(',')
      .map(v => v.trim())
      .filter(v => v !== '')
      .map(v => parseInt(v))
      .filter(v => !isNaN(v));
      
    onChange({
      ...value,
      value_safe: values
    });
  };

  if (isRangeOperator) {
    const display = secondsToDisplay(currentValue as number);
    
    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={display.minutes}
            onChange={(e) => handleSingleValueChange(e.target.value, display.seconds)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
            placeholder="0"
            min="0"
            style={{
              fontSize: '13px',
              fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
            }}
          />
          <span className="text-gray-500">:</span>
          <input
            type="number"
            value={display.seconds}
            onChange={(e) => handleSingleValueChange(display.minutes, e.target.value)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
            placeholder="00"
            min="0"
            max="59"
            style={{
              fontSize: '13px',
              fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
            }}
          />
          <Typography variant="smallParagraph" color="#6B7280">
            min:sec
          </Typography>
        </div>
      </div>
    );
  }

  if (isMultiValueOperator) {
    const displayValue = Array.isArray(currentValue) ? currentValue.join(', ') : '';
    
    return (
      <div className="w-full">
        <input
          type="text"
          value={displayValue}
          onChange={(e) => handleMultiValueChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter durations in seconds separated by commas..."
          style={{
            fontSize: '13px',
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
          }}
        />
        <div className="mt-1">
          <Typography variant="smallParagraph" color="#6B7280">
            Separate multiple durations with commas (e.g., 30, 60, 120 for 30s, 1min, 2min)
          </Typography>
        </div>
      </div>
    );
  }

  // Fallback for unknown operators
  const display = secondsToDisplay(currentValue as number);
  
  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={display.minutes}
          onChange={(e) => handleSingleValueChange(e.target.value, display.seconds)}
          className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
          placeholder="0"
          min="0"
          style={{
            fontSize: '13px',
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
          }}
        />
        <span className="text-gray-500">:</span>
        <input
          type="number"
          value={display.seconds}
          onChange={(e) => handleSingleValueChange(display.minutes, e.target.value)}
          className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
          placeholder="00"
          min="0"
          max="59"
          style={{
            fontSize: '13px',
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
          }}
        />
        <Typography variant="smallParagraph" color="#6B7280">
          min:sec
        </Typography>
      </div>
    </div>
  );
} 